import styles from './Wholesaledata.module.css';
import MenuOptions from '../MenuOptions';
import { useNavigate } from 'react-router-dom';
import SiteSelector from 'components/Shared/SiteSelector';
import { AccountServiceInfo, AccountServiceStatusForDisplay, DefaultDisplayDateFormat, EuropaAccountServiceStatus, WholesaleIntervalDataRequestModel, Site } from 'data/CustomerPortalTypes';
import {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentAccount } from 'store/userSlice';
import { shouldBeDisabledByDayDateRange, toSite, toUtcStartOfDay } from 'utilities/Utility';
import { downLoadWholesaleIntervalDataCsvFile, getExistWolesaleAccountServices } from 'services/RequestDataService';
import { DatePicker } from 'antd';
import { RangeValue } from 'rc-picker/lib/interface';
import moment, { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { fetchExistWolesaleAccountServices, getCurrentAccountExistWolesaleAccountServices } from 'store/existWolesaleAccountServicesSlice';
const { RangePicker } = DatePicker;

const activeServiceStatus: EuropaAccountServiceStatus[] = [EuropaAccountServiceStatus.PendingSwitchOut, EuropaAccountServiceStatus.Switched];
const emptyRange = null as RangeValue<Moment>;
const ServiceDateFormatYMD = "YYYY/MM/DD";

const Wholesale = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentAccount = useSelector(selectCurrentAccount);
    const [siteList, setSiteList] = useState<Site[]>([]);
    const [currentAccountService, setCurrentAccountService] = useState<AccountServiceInfo | null>(null);
    const currentAccountExistWolesaleAccountServices = useSelector(getCurrentAccountExistWolesaleAccountServices);
    const [currentDateRange, setCurrentDateRange] = useState(emptyRange);
    const [dateRangeIsValid, setDateRangeIsValid] = useState<boolean | undefined>(undefined);

    const startDate = toUtcStartOfDay(moment('2024-01-01', 'YYYY-MM-DD'));
    const yesterday = toUtcStartOfDay(moment().subtract(1, 'days'))
    const disabledDates = (date: Moment)=>{
        return shouldBeDisabledByDayDateRange(date, startDate, yesterday)       
    }
     
    const rangeSelected = (range: RangeValue<Moment>) => {
   
        const start = range&&range[0] ? toUtcStartOfDay(range[0]) : null;
        const end =  range&&range[1] ? toUtcStartOfDay(range[1]) : null;
        setCurrentDateRange([start, end]);
        if(!start || !end){
            setDateRangeIsValid(undefined)
            return;
        }
        if(start!.clone().add(2, 'months').isBefore(end!, 'day')){
            setDateRangeIsValid(false)
        }
        else{
            setDateRangeIsValid(true)
        }
    }

    const siteSelected = (accountService: AccountServiceInfo | null) => {

        setCurrentAccountService(accountService);
    }

    const downLoadCsvFileLocal = () => {
        const site = siteList.find(s => s.accountServiceInfo === currentAccountService);
        if (!site) {
            return;
        }

       const requestData = new WholesaleIntervalDataRequestModel
       (
            site.accountServiceInfo.accountServiceId,
            site.nmi,
            currentDateRange ? currentDateRange[0] ? currentDateRange[0]!.format(ServiceDateFormatYMD) : null : null,
            currentDateRange ? currentDateRange[1] ? currentDateRange[1]!.format(ServiceDateFormatYMD) : null : null)

        downLoadWholesaleIntervalDataCsvFile(requestData);
    }
    
    useEffect(() => {
        if (!currentAccount?.services?.length) {
            navigate('/Dashboard');
            return
        }

        if(!currentAccountExistWolesaleAccountServices){
            dispatch(fetchExistWolesaleAccountServices({accountId: currentAccount.accountId, accountServiceIds: currentAccount.services.map(x => x.accountServiceId)}));
            return;
        }

        if((currentAccountExistWolesaleAccountServices?.length ?? 0) === 0){
            navigate('/Dashboard');
            return
        }

        const sortSiteList = (siteList: Site[]) => {
            return siteList.sort((site1, site2) => {
                // reverse(): to make sure the service with status not in the list is at the bottom
                const allServiceStatus = Object.keys(AccountServiceStatusForDisplay).reverse();
                const index1 = allServiceStatus.indexOf(site1.accountServiceInfo.status);
                const index2 = allServiceStatus.indexOf(site2.accountServiceInfo.status);
                return index2 - index1;
            })
        }
        
        const services =  currentAccount.services.filter(x=> currentAccountExistWolesaleAccountServices?.includes(x.accountServiceId))
        const siteList = toSite(services)
        const sortedSiteList = sortSiteList(siteList)
        setSiteList(sortedSiteList);
        
    }, [currentAccount, currentAccountExistWolesaleAccountServices]);

    useEffect(() => {
        if(siteList.length === 1){
            siteSelected(siteList[0].accountServiceInfo) 
            return;
        }
        if (siteList.length) {
            const activeSites = siteList.filter(s => activeServiceStatus.includes(s.accountServiceInfo.status as EuropaAccountServiceStatus));
            if(activeSites.length === 1){
                siteSelected(activeSites[0].accountServiceInfo);
                return;
            }
        }
        siteSelected(null)
    }, [siteList])


    return ( 
        <div className={styles["wholesale-data-block"]}>
            <div className="globird-menu-option">
                <p className="globird-menu-label">Show me: </p>
                <MenuOptions
                    id="WholesaleDataMenuSelector"
                    defaultIndex={3} />
            </div>
            <div className={styles["wholesaledata-item"]}>
                <p>Choose your WHOLESAVE electricity service</p>
                <SiteSelector
                    onChange={s => s && siteSelected(s.accountServiceInfo)}
                    sites={siteList}
                    currentSite={siteList?.find(s => s.accountServiceInfo === currentAccountService)}
                    className={styles["wholesaledata-item-select"]}
                />
            </div>
            {
                currentAccountService &&
                <>
                    <div className={styles["wholesaledata-item"]}>
                        <p>Select Usage Period</p>
                        <div
                            className='flex-left-center'>
                            <RangePicker
                                value={currentDateRange}
                                onChange={rangeSelected}
                                disabledDate={disabledDates}
                                format={DefaultDisplayDateFormat} />
                        </div>
                        {dateRangeIsValid === false && <span style={{ color: 'red' }}>Please select a date range of up to 2 months. For data covering a longer period, you may download it in multiple batches.</span>}
                    </div>
                     <p style={{ width: "100%", textAlign: "center" }}>
                        <FontAwesomeIcon
                            icon={faDownload}
                            size="2x"
                            title="download meter read summary"
                            onClick={!dateRangeIsValid ? undefined : downLoadCsvFileLocal}
                            style={{
                                cursor: !dateRangeIsValid ? 'not-allowed' : 'pointer',
                                marginRight: '30px',
                                color: !dateRangeIsValid  ? 'gray' : '#e71d73'
                            }}
                        />
                    </p>
                    <div>
                        <p className={styles['wholesaledata-important-note-summary']}>Please note:</p>
                        <ul className={styles['wholesaledata-important-note']}>
                            <li>
                            The data provided in this download is calculated based on the formulas outlined in your Welcome Pack.
                            </li>
                            <li>
                            Usage data is aggregated at the NMI level and reflects the sum of all meters linked to the selected service.
                            </li>
                            <li>
                            All timestamps are shown in Australian Eastern Standard Time (AEST) and are aligned with your billing periods.
                            </li>
                            <li>
                            Wholesale feed-in rates and energy costs are calculated based on applicable market or fixed rates, depending on your state.
                            </li>
                            <li>
                            Rounding may occur at various calculation points and may result in minor differences compared to your invoice totals.
                            </li>
                            <li>
                            Data is subject to the terms and conditions outlined in your agreement with GloBird Energy.
                            </li>
                        </ul>
                    </div>
                </>
            }
        </div>
        
    )

}

export default Wholesale;